import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Calendar as CalendarIcon, Plus, List } from 'lucide-react';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { format } from 'date-fns';

const Dashboard = ({ user }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date()
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, [dateRange]);

  const fetchStats = async () => {
    // Handle undefined dates from calendar
    if (!dateRange?.from || !dateRange?.to) return;
    
    try {
      const fromDate = format(dateRange.from, 'yyyy-MM-dd');
      const toDate = format(dateRange.to, 'yyyy-MM-dd');
      const response = await axios.get(`${API}/sales/dashboard?from_date=${fromDate}&to_date=${toDate}`, {
        withCredentials: true
      });
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (range) => {
    // If only one date is selected, use it for both from and to
    if (range?.from && !range?.to) {
      setDateRange({ from: range.from, to: range.from });
    } else if (range?.from && range?.to) {
      setDateRange(range);
    }
  };

  if (loading) {
    return <div className="loading-screen"><div className="spinner"></div></div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>Dashboard</h1>
          <p style={{ color: '#666' }}>Welcome back, {user.name}!</p>
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <button className="btn btn-secondary" data-testid="date-range-picker">
              <CalendarIcon size={16} />
              {dateRange?.from && dateRange?.to ? 
                `${format(dateRange.from, 'MMM dd')} - ${format(dateRange.to, 'MMM dd, yyyy')}` 
                : 'Select date range'}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={dateRange}
              onSelect={handleDateSelect}
              numberOfMonths={2}
            />
          </PopoverContent>
        </Popover>
      </div>

      <div className="stats-grid" data-testid="stats-grid">
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/orders?tab=all')} data-testid="stat-total-orders">
          <div className="stat-label">Total Orders</div>
          <div className="stat-value">{stats?.total || 0}</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/orders?tab=pending')} data-testid="stat-pending">
          <div className="stat-label">Pending</div>
          <div className="stat-value" style={{ color: '#997404' }}>{stats?.pending || 0}</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/orders?tab=approved')} data-testid="stat-approved">
          <div className="stat-label">Approved</div>
          <div className="stat-value" style={{ color: '#065F46' }}>{stats?.approved || 0}</div>
        </div>
        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/app/orders?tab=rejected')} data-testid="stat-rejected">
          <div className="stat-label">Rejected</div>
          <div className="stat-value" style={{ color: '#991B1B' }}>{stats?.rejected || 0}</div>
        </div>
        <div className="stat-card" style={{ borderLeftColor: '#4CAF50', cursor: 'pointer' }} onClick={() => navigate('/app/spiffs')} data-testid="stat-spiff">
          <div className="stat-label">Total Spiff Earned</div>
          <div className="stat-value" style={{ color: '#4CAF50' }}>${stats?.totalSpiff || 0}</div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ fontSize: '20px', fontWeight: '700', marginBottom: '16px' }}>Quick Actions</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
          <a href="/app/create" style={{ textDecoration: 'none' }}>
            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, #FDB515 0%, #e4a50e 100%)',
              borderRadius: '16px',
              color: '#000',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}>
              <Plus size={24} style={{ marginBottom: '8px' }} />
              <div>Create New Order</div>
            </div>
          </a>
          <a href="/app/create-custom" style={{ textDecoration: 'none' }}>
            <div style={{
              padding: '24px',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              borderRadius: '16px',
              color: '#FFF',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}>
              <Plus size={24} style={{ marginBottom: '8px' }} />
              <div>Create Custom Order</div>
            </div>
          </a>
          <button 
            style={{
              padding: '24px',
              background: '#F5F5F5',
              borderRadius: '16px',
              color: '#1A1A1A',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              border: 'none',
              textDecoration: 'none',
              width: '100%'
            }}
            onClick={() => navigate('/app/orders?tab=all')}
          >
            <List size={24} style={{ marginBottom: '8px', display: 'block' }} />
            <div>View Orders</div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
